import React from 'react';
import { AlertTriangle, ArrowLeft, Loader2, RefreshCw, WifiOff } from 'lucide-react';
import { WebsiteHeader } from '../../Dashboard/WebsiteHeader';
import { LoadingAnimation } from '../LoadingAnimation';
function Shell({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex min-h-screen w-full flex-col bg-forensic-bg text-white" data-id="element-317" data-mp="3jzmxn2wundf0">
      <WebsiteHeader data-id="element-318" data-mp="3k1zok5kl4y47" />
      <main className="flex flex-1 items-center justify-center px-5 py-16" data-id="element-319" data-mp="1tqr8wqp143u">
        {children}
      </main>
    </div>;
}
const PANEL = 'w-full max-w-md rounded-2xl border border-forensic-line bg-forensic-surface p-8 text-center';
export function ReportLoadingState() {
  return <Shell data-id="element-320" data-mp="6kqltvccba36">
      <div className="flex flex-col items-center gap-4" role="status" data-id="element-321" data-mp="6l0exrsolxd3">
        <Loader2 className="animate-spin text-forensic-muted" size={28} aria-hidden="true" data-id="element-322" data-mp="6mtk12nnc9iz" />
        <p className="text-sm text-forensic-muted" data-id="element-323" data-mp="kif6p36eqh88">Retrieving case file…</p>
      </div>
    </Shell>;
}
export function ReportProcessingState({
  onBack
}: {
  onBack: () => void;
}) {
  return <Shell data-id="element-324" data-mp="fy25txy2pxir">
      <div className="flex flex-col items-center text-center" role="status" data-id="element-325" data-mp="fybvtlj4c4og">
        <LoadingAnimation status="processing" data-id="element-326" data-mp="jycly3fky4bc" />
        <p className="mt-8 text-base font-medium text-white" data-id="element-327" data-mp="ox74d49mxqux">
          Analyzing communication patterns…
        </p>
        <p className="mt-2 max-w-sm text-sm text-forensic-muted" data-id="element-328" data-mp="twaaks1cq34h">
          This usually takes under a minute. You can leave — the case file will be on your dashboard.
        </p>
        <button type="button" onClick={onBack} className="mt-6 inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-forensic-muted transition-colors duration-150 ease-out hover:text-white" data-id="element-329" data-mp="12wgm48dhot10">
          <ArrowLeft size={16} aria-hidden="true" data-id="element-330" data-mp="1cm05df546owe" />
          Back to dashboard
        </button>
      </div>
    </Shell>;
}
type ErrorKind = 'not_found' | 'no_id' | 'network' | 'session' | 'generic';
const ERROR_COPY: Record<ErrorKind, {
  title: string;
  body: string;
}> = {
  no_id: {
    title: 'No case file selected',
    body: 'Open a scan from your dashboard to view its forensic report.'
  },
  not_found: {
    title: 'Report unavailable',
    body: 'This case file doesn’t exist or isn’t linked to your account.'
  },
  network: {
    title: 'Connection lost',
    body: 'We couldn’t reach ToxicDetector. Check your connection and try again.'
  },
  session: {
    title: 'Session expired',
    body: 'For your privacy, you were signed out. Sign in again to view this report.'
  },
  generic: {
    title: 'Analysis interrupted',
    body: 'We couldn’t load this forensic report.'
  }
};
export function classifyReportError(message: string | null | undefined): ErrorKind {
  const msg = (message || '').toLowerCase();
  if (!msg) return 'not_found';
  if (/network|fetch|offline|timeout/.test(msg)) return 'network';
  if (/jwt|session|auth|expired|sign in/.test(msg)) return 'session';
  if (/not found|no rows|0 rows/.test(msg)) return 'not_found';
  return 'generic';
}
export function ReportErrorState({
  kind,
  onRetry,
  onBack,
  onSignIn
}: {
  kind: ErrorKind;
  onRetry?: () => void;
  onBack: () => void;
  onSignIn?: () => void;
}) {
  const copy = ERROR_COPY[kind];
  const Icon = kind === 'network' ? WifiOff : AlertTriangle;
  return <Shell data-id="element-331" data-mp="1x475droo09ma">
      <div className={PANEL} data-id="element-332" data-mp="1x3yqxvcw8w8z">
        <Icon className="mx-auto text-forensic-crimson-soft" size={28} aria-hidden="true" data-id="element-333" data-mp="1x35ubn7nfn4k" />
        <h1 className="mt-4 text-xl font-semibold tracking-tight" data-id="element-334" data-mp="1j3e8j0aw52g0">{copy.title}</h1>
        <p className="mt-2 text-sm text-forensic-muted" data-id="element-335" data-mp="1n7lyf0t34oft">{copy.body}</p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center" data-id="element-336" data-mp="1971h692tj0of">
          {kind === 'session' && onSignIn ? <PrimaryButton onClick={onSignIn} data-id="element-337" data-mp="1e6xk04zs4m2e">Sign in</PrimaryButton> : onRetry && <PrimaryButton onClick={onRetry} data-id="element-338" data-mp="33luce6zshpfu">Try again</PrimaryButton>}
          <SecondaryButton onClick={onBack} data-id="element-339" data-mp="38kfbg33b232p">Dashboard</SecondaryButton>
        </div>
      </div>
    </Shell>;
}

/** A failed scan can't be restarted from the client — the user runs a new scan. */
export function ReportFailedState({
  onRetry,
  onBack
}: {
  onRetry: () => void;
  onBack: () => void;
}) {
  return <Shell data-id="element-340" data-mp="zfwsvv4nwh90">
      <div className={PANEL} data-id="element-341" data-mp="zfn3o9rfye4d">
        <AlertTriangle className="mx-auto text-forensic-crimson-soft" size={28} aria-hidden="true" data-id="element-342" data-mp="zisqficll3wx" />
        <h1 className="mt-4 text-xl font-semibold tracking-tight" data-id="element-343" data-mp="uk7nhb5yo64s">Analysis interrupted</h1>
        <p className="mt-2 text-sm text-forensic-muted" data-id="element-344" data-mp="pkv450eath6y">
          We couldn’t complete this forensic analysis. This can happen with
          very short or unreadable conversations.
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center" data-id="element-345" data-mp="fqw3k5tvi5h4">
          <PrimaryButton onClick={onRetry} data-id="element-346" data-mp="bsyfx7fr3rfu">
            <RefreshCw size={16} aria-hidden="true" data-id="element-347" data-mp="6s0xquuqqxec" />
            Run a new scan
          </PrimaryButton>
          <SecondaryButton onClick={onBack} data-id="element-348" data-mp="1thc6vk27nmi">Dashboard</SecondaryButton>
        </div>
      </div>
    </Shell>;
}
function PrimaryButton({
  children,
  onClick
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-forensic-crimson px-6 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-forensic-crimson-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-forensic-crimson-soft" data-id="element-349" data-mp="2iecjmshvdoea">
      {children}
    </button>;
}
function SecondaryButton({
  children,
  onClick
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="inline-flex min-h-[48px] items-center justify-center rounded-xl border border-forensic-line-strong px-6 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:border-white/30" data-id="element-350" data-mp="23pgeb3776phd">
      {children}
    </button>;
}